import { createSlice, PayloadAction } from "@reduxjs/toolkit";
import { CartLine } from "../../types/ordering";

const STORAGE_KEY = "ci_cart_v1";
/** contract: ek quote me max 50 line */
const MAX_LINES = 50;

export interface CartState {
  lines: CartLine[];
  /** ek checkout attempt ka UUID — double click par doosra order na bane */
  clientRef: string | null;
  /** aakhri quote ka token (15 min valid). Cart badalte hi null. */
  quoteToken: string | null;
}

const emptyState: CartState = { lines: [], clientRef: null, quoteToken: null };

export const makeClientRef = (): string => {
  try {
    const c: any = typeof crypto !== "undefined" ? crypto : null;
    if (c && typeof c.randomUUID === "function") return c.randomUUID();
  } catch (e) {
    // ignore
  }
  return `web-${Date.now()}-${Math.random().toString(36).slice(2, 10)}`;
};

const cleanLine = (l: any): CartLine | null => {
  if (!l || typeof l.companyProductId !== "string") return null;
  const qty = Number(l.quantity);
  if (!isFinite(qty) || qty <= 0) return null;
  return {
    companyProductId: l.companyProductId,
    quantity: qty,
    itemId: typeof l.itemId === "string" ? l.itemId : undefined,
    companyId: typeof l.companyId === "string" ? l.companyId : undefined,
    itemName: typeof l.itemName === "string" ? l.itemName : undefined,
    companyName: typeof l.companyName === "string" ? l.companyName : undefined,
    itemCode: typeof l.itemCode === "string" ? l.itemCode : undefined,
    unit: typeof l.unit === "string" ? l.unit : undefined,
    image: typeof l.image === "string" ? l.image : undefined,
    mockHintPrice:
      typeof l.mockHintPrice === "number" && l.mockHintPrice > 0
        ? l.mockHintPrice
        : undefined,
  };
};

export const loadCart = (): CartState => {
  try {
    const raw = localStorage.getItem(STORAGE_KEY);
    if (!raw) return { ...emptyState };
    const parsed = JSON.parse(raw);
    if (!parsed || !Array.isArray(parsed.lines)) return { ...emptyState };
    const lines = parsed.lines
      .map(cleanLine)
      .filter((l: CartLine | null): l is CartLine => l !== null)
      .slice(0, MAX_LINES);
    return {
      lines,
      clientRef: typeof parsed.clientRef === "string" ? parsed.clientRef : null,
      quoteToken: null,
    };
  } catch (e) {
    return { ...emptyState };
  }
};

export const saveCart = (state: CartState): void => {
  try {
    localStorage.setItem(
      STORAGE_KEY,
      JSON.stringify({ lines: state.lines, clientRef: state.clientRef })
    );
  } catch (e) {
    // private window / storage full — chup-chaap chhod do
  }
};

const qty3 = (n: number): number => Math.round(n * 1000) / 1000;

const cartSlice = createSlice({
  name: "cart",
  initialState: loadCart(),
  reducers: {
    addToCart: (state, action: PayloadAction<CartLine>) => {
      const incoming = cleanLine({
        ...action.payload,
        quantity: Number(action.payload.quantity) > 0 ? action.payload.quantity : 1,
      });
      if (!incoming) return;
      const found = state.lines.find(
        (l) => l.companyProductId === incoming.companyProductId
      );
      if (found) {
        found.quantity = qty3(found.quantity + incoming.quantity);
        found.itemName = incoming.itemName || found.itemName;
        found.companyName = incoming.companyName || found.companyName;
        found.itemCode = incoming.itemCode || found.itemCode;
        found.unit = incoming.unit || found.unit;
        found.image = incoming.image || found.image;
        found.itemId = incoming.itemId || found.itemId;
        found.companyId = incoming.companyId || found.companyId;
        found.mockHintPrice = incoming.mockHintPrice ?? found.mockHintPrice;
      } else {
        if (state.lines.length >= MAX_LINES) return;
        state.lines.push(incoming);
      }
      state.quoteToken = null;
    },
    setQuantity: (
      state,
      action: PayloadAction<{ companyProductId: string; quantity: number }>
    ) => {
      const { companyProductId, quantity } = action.payload;
      const q = Number(quantity);
      const found = state.lines.find((l) => l.companyProductId === companyProductId);
      if (!found) return;
      if (!isFinite(q) || q <= 0) {
        state.lines = state.lines.filter(
          (l) => l.companyProductId !== companyProductId
        );
      } else {
        found.quantity = qty3(q);
      }
      state.quoteToken = null;
    },
    stepQuantity: (
      state,
      action: PayloadAction<{ companyProductId: string; by: number }>
    ) => {
      const found = state.lines.find(
        (l) => l.companyProductId === action.payload.companyProductId
      );
      if (!found) return;
      const next = qty3(found.quantity + Number(action.payload.by));
      if (next <= 0) {
        state.lines = state.lines.filter(
          (l) => l.companyProductId !== action.payload.companyProductId
        );
      } else {
        found.quantity = next;
      }
      state.quoteToken = null;
    },
    removeFromCart: (state, action: PayloadAction<string>) => {
      state.lines = state.lines.filter(
        (l) => l.companyProductId !== action.payload
      );
      state.quoteToken = null;
    },
    clearCart: (state) => {
      state.lines = [];
      state.quoteToken = null;
      state.clientRef = null;
    },
    setQuoteToken: (state, action: PayloadAction<string | null>) => {
      state.quoteToken = action.payload;
    },
    /** cart khulte waqt ek baar — phir poore checkout me wahi clientRef */
    ensureClientRef: (state) => {
      if (!state.clientRef) state.clientRef = makeClientRef();
    },
    resetClientRef: (state) => {
      state.clientRef = makeClientRef();
    },
  },
});

export const {
  addToCart,
  setQuantity,
  stepQuantity,
  removeFromCart,
  clearCart,
  setQuoteToken,
  ensureClientRef,
  resetClientRef,
} = cartSlice.actions;

export const selectCartLines = (state: { cart: CartState }): CartLine[] =>
  state.cart.lines;
export const selectCartCount = (state: { cart: CartState }): number =>
  state.cart.lines.length;
export const selectClientRef = (state: { cart: CartState }): string | null =>
  state.cart.clientRef;

export default cartSlice.reducer;
